import { request } from '@/lib/api'
import type { SaveOutcome } from './api'
import type { FormValues } from './types'

/**
 * Сохранение карточки доменным PUT с версией (#98, #99).
 *
 * Карточка редактирует колонки каталога (`last_name`), а доменный эндпоинт принимает поля
 * сущности (`lastName`). Неизвестная колонка не отправляется молча, а отвергается: иначе
 * правка поля, которого PUT не знает, «сохранялась» бы, ничего не записав.
 *
 * Все поля уходят одной транзакцией, поэтому «сохранено частично» здесь не бывает: либо
 * легло всё, либо ничего. Правка поверх чужой приходит 409, и карточка показывает
 * конфликт, а не затирает чужое.
 */
export interface FieldMapping {
  /** Имя поля в теле PUT. */
  field: string
  /** Приведение значения формы к тому, что ждёт сервер. */
  convert?: (value: unknown) => unknown
}

export function makeDomainSave(
  urlOf: (id: string) => string,
  columns: Record<string, string | FieldMapping>,
) {
  return async function save(
    _entity: string,
    id: string,
    changes: FormValues,
    expectedVersion: number | null,
  ): Promise<SaveOutcome> {
    const changed = Object.keys(changes)
    const unknown = changed.find((column) => !(column in columns))
    if (unknown) {
      return { applied: [], failure: { field: unknown, error: new Error('card.notEditable') } }
    }
    const body: Record<string, unknown> = { version: expectedVersion }
    for (const column of changed) {
      const mapping = columns[column] as string | FieldMapping
      const target = typeof mapping === 'string' ? { field: mapping } : mapping
      const value = changes[column]
      body[target.field] = target.convert ? target.convert(value) : value
    }
    try {
      await request(urlOf(id), { method: 'PUT', body })
      return { applied: changed, failure: null }
    } catch (error) {
      return { applied: [], failure: { field: changed[0] ?? '', error } }
    }
  }
}

/**
 * Пустое значение — строка, а не null: на сервере null значит «не менялось», а пустая
 * строка — «очистить». Стёртое в карточке поле иначе молча осталось бы прежним.
 */
export function asClearableString(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}
